import React, {useEffect, useMemo, useState} from 'react';

import {useSelector} from 'react-redux';
import {WithRouterProps} from 'react-router';

import {AccountPageShell} from 'app/account/components/AccountPageShell';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {
  SelectDateRangeForm,
  VALID_RANGE_TEMPLATE,
} from 'app/general/widgets/SelectDateRangeForm';
import StateButton from 'app/general/widgets/StateButton';
import {useReportDetail, useRunReport} from 'app/reports/api';
import {getReportBySlugSelector} from 'app/reports/slice';
import {SearchItemsSelectSingle} from 'app/search/components/SearchItemsSelect';
import {PROCEDURES_API_URL, PROCESS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {SearchSerializedType} from 'sweet-types';

interface Props extends WithRouterProps<{slug: string}, unknown> {
  item?: string;
}

export function ReportsDetailPage({params, router}: Props): JSX.Element {
  const [timeRange, setTimeRange] = useState<{end: string; start: string}>({
    start: '',
    end: '',
  });
  const [documentValue, setDocumentValue] =
    useState<SearchSerializedType | null>(null);
  const [
    runReport,
    {
      data: submittedJob,
      isError: isErrorSubmittingReport,
      isLoading: isLoadingReport,
      reset,
    },
  ] = useRunReport();
  const report = useSelector(getReportBySlugSelector(params.slug));
  const {error, isLoading} = useReportDetail(params.slug);

  const documentAPI = {
    procedure: PROCEDURES_API_URL,
    proceduregroup: PROCESS_API_URL,
  };

  const documentURL = documentValue
    ? `${document.location.origin}${documentAPI[documentValue.content_type]}${
        documentValue.object_id
      }/`
    : '';

  const isDocumentVisible = params.slug == 'document-taskinstance';

  const isFormValid = isDocumentVisible
    ? Boolean(timeRange && documentValue)
    : Boolean(timeRange);

  const runReportParams = {
    starts_at: timeRange.start,
    ends_at: timeRange.end,
    slug: params.slug,
    ...(isDocumentVisible && {document_url: documentURL}),
  };

  useEffect(() => {
    if (submittedJob?.result?.url && report != null) {
      router.push(submittedJob.result.url);
    }
  }, [submittedJob]);

  const label = useMemo(
    () =>
      report?.slug != null
        ? {
            'teammates-team-summary':
              'When a member joined or when they have used SweetProcess.',
            'list-documents': 'When the document was created.',
          }[report.slug]
        : undefined,
    [report?.slug]
  );

  return (
    <AccountPageShell
      location="reports"
      title={report?.name}
      content={report}
      error={error}
      isLoading={isLoading}
      errorContent={{head: gt('Could not load the report')}}
    >
      {() => (
        <>
          {isErrorSubmittingReport ? (
            <NoResultsMessage
              icon="fa-exclamation-triangle text-warning"
              head={gt("Couldn't create the report")}
            >
              <p className="lead mb-0">
                {gt('There was an error creating your report.')}
              </p>
              <p className="lead">
                {gt('Our system has been notified of the problem.')}
              </p>
              <button className="btn btn-primary ml-auto" onClick={reset}>
                {gt('Retry Report')}
              </button>
            </NoResultsMessage>
          ) : isLoadingReport ? (
            <NoResultsMessage
              icon="fa-circle-notch fa-spin"
              head={gt('Generating report...')}
            >
              <p>
                {gt('This can take some minutes, you can continue working.')}
              </p>
              <p>{gt('We will notify you when your report is ready.')}</p>
            </NoResultsMessage>
          ) : (
            <>
              {isDocumentVisible && (
                <div className="mb-2">
                  <label>{gt('Document')}</label>
                  <SearchItemsSelectSingle
                    selectProps={{
                      autoFocus: true,
                      isClearable: false,
                    }}
                    placeholder={gt('Select a procedure or process...')}
                    params={{
                      ct: ['procedure', 'proceduregroup'].join(','),
                      ordering: 'name',
                    }}
                    selected={documentValue}
                    onChange={(documentValue) =>
                      Promise.resolve(setDocumentValue(documentValue))
                    }
                  />
                </div>
              )}
              <SelectDateRangeForm
                label={label}
                onChange={setTimeRange}
                initialValue="this-month"
                templates={availableTemplates}
                options={{position: 'below'}}
              />
              <div className="border-top mt-3 pt-4">
                <StateButton
                  busyText={gt('Run Report')}
                  className="btn btn-primary ml-auto"
                  disabled={!isFormValid}
                  onClick={() => runReport(runReportParams)}
                >
                  {gt('Run Report')}
                </StateButton>
              </div>
            </>
          )}
        </>
      )}
    </AccountPageShell>
  );
}

const availableTemplates: Array<VALID_RANGE_TEMPLATE> = [
  'this-week',
  'this-month',
  'last-month',
  'this-quarter',
  'last-quarter',
  'this-year',
  'all-time',
  'custom',
];
